import { useRef, useState } from "react";
import { Paperclip, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtBytes } from "../../lib/support";

const MAX = 5;

export const AttachmentPicker = ({ value = [], onChange, testId = "attachment" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (value.length + files.length > MAX) {
      toast.error(`Up to ${MAX} attachments`);
      return;
    }
    setBusy(true);
    const added = [];
    for (const file of files) {
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/support/attachments", form);
        added.push(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Could not attach ${file.name}`);
      }
    }
    setBusy(false);
    if (added.length) onChange([...value, ...added]);
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain"
        onChange={pick}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy || value.length >= MAX}
        data-testid={`${testId}-add-btn`}
        className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-zinc-600 hover:bg-zinc-900 disabled:opacity-40 text-zinc-300 px-3 py-2 transition-colors"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
        {busy ? "Uploading" : "Attach screenshot"}
      </button>
      {value.length > 0 && (
        <ul className="space-y-1" data-testid={`${testId}-list`}>
          {value.map((a) => (
            <li
              key={a.attachment_id}
              className="flex items-center gap-2 text-xs text-zinc-400 border border-zinc-800 bg-zinc-900/40 px-3 py-2"
            >
              <Paperclip className="w-3 h-3 text-zinc-600 shrink-0" />
              <span className="truncate flex-1">{a.file_name}</span>
              <span className="font-mono text-[10px] text-zinc-600">{fmtBytes(a.size)}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x.attachment_id !== a.attachment_id))}
                data-testid={`${testId}-remove-${a.attachment_id}`}
                className="text-zinc-600 hover:text-red-400 transition-colors"
                aria-label="Remove attachment"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="text-[10px] font-mono text-zinc-600">
        PNG · JPG · WEBP · GIF · PDF · TXT · up to 5 MB each
      </div>
    </div>
  );
};

export const AttachmentLinks = ({ items = [], testId = "attachment-link" }) => {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((a) => (
        <a
          key={a.attachment_id}
          href={`/api/support/attachments/${a.attachment_id}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testId}-${a.attachment_id}`}
          className="inline-flex items-center gap-1.5 text-[11px] border border-zinc-800 hover:border-blue-500/50 hover:text-blue-300 text-zinc-400 px-2.5 py-1.5 transition-colors"
        >
          <Paperclip className="w-3 h-3" />
          <span className="max-w-[180px] truncate">{a.file_name}</span>
          <span className="font-mono text-[10px] text-zinc-600">{fmtBytes(a.size)}</span>
        </a>
      ))}
    </div>
  );
};
